import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../../../components/form-controls/Button";
import Switch from "../../../components/form-controls/Switch";
import Input from "../../../components/form-controls/Input";
import Select from 'react-select'
import TextArea from "../../../components/form-controls/TextArea";
import { ShortLinksService } from "../../../services/ShortLinksService";
import { AlertService } from "../../../services/Alert";
import { SubAccountService } from '../../../services/SubaccountService';
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import useModal from "../../../hooks/useModal";
import InvalidFeedback from "../../../components/InvalidFeedback";
import { useLocation } from "react-router-dom";
import { APPS } from "../../../constants";
import { useTranslation } from "react-i18next";
import Calendar from "../../../components/Calendar";
interface ModalCreateLinkProps {
    showModalLink: boolean;
    onRequestClose: (refresh: boolean) => void;
}
const CreateLinkModal = ({ showModalLink, onRequestClose }: ModalCreateLinkProps) => {

    const { t } = useTranslation();

    const { account } = useCurrentAccount();

    const { state: { channel } } = useLocation()

    const modalCreateLink = useRef<HTMLDivElement>(null);

    useModal(modalCreateLink, showModalLink)

    const [subAccounts, setsubAccounts] = useState([]);
    const opciones = [
        { label: t("platform.channels.links-page.create-modal.trigger-options.sent_subaccount"), value: "sent_subaccount" },
        { label: t("platform.channels.links-page.create-modal.trigger-options.sent_agent"), value: "sent_agent" },
        { label: t("platform.channels.links-page.create-modal.trigger-options.none"), value: "none" }
    ];


    const defaultValues = {
        message: '',
        trigger: '',
        label: '',
        sub_account_id: 0,
        advanced_follow: false,
        app: channel.toUpperCase() === APPS.WHATSAPP_STANDAR ? 'waweb' : 'wa-business',
        ttl_enabled: false,
        ttl_date: null,
        message_ttl: '',
    }

    const { control, register, watch, setValue, handleSubmit, reset, formState: { errors, isValid, isSubmitting } } = useForm({
        mode: 'onChange',
        defaultValues
    });

    const advancedFollow = watch('advanced_follow');
    const subAccount = watch('sub_account_id');
    const trigger = watch('trigger');
    const ttlEnabled = watch('ttl_enabled');


    const getSubAccounts = async () => {
        const { data } = await SubAccountService.getSubaccounts(account.id);
        setsubAccounts(data.sub_accounts.map(subaccount => ({
            value: subaccount.id,
            label: subaccount.name
        })));
    };

    const submit = async (data) => {
        try {
            data.advanced_follow = data.advanced_follow ? 1 : 0;
            let expireAt = null;
            if (data.ttl_enabled && data.ttl_date) {
                expireAt = data.ttl_date.toISOString().split('T')[0];
            }
            await ShortLinksService.save(account.id, data.message, data.app, data.trigger, data.label, data.sub_account_id, data.advanced_follow, expireAt, data.message_ttl);
            await onRequestClose(true);
            AlertService.toastSuccess(t("platform.channels.links-page.create-modal.toast-save-success"));
            reset(defaultValues);
        } catch (error) {
            console.error(error);
            AlertService.error('¡Error!', error)
        }
    }

    const handleTTLDateChange = (date) => {
        setValue('ttl_date', date, { shouldValidate: true });
    };

    useEffect(() => {
        getSubAccounts();
    }, []);

    useEffect(() => {
        if (!advancedFollow) {
            setValue('advanced_follow', false);
            setValue('label', defaultValues.label);
            setValue('sub_account_id', defaultValues.sub_account_id);
            setValue('trigger', defaultValues.trigger);
        }

    }, [advancedFollow, setValue]);

    useEffect(() => {
        if (!trigger) {
            setValue('trigger', '');
        }
    }, [trigger]);

    useEffect(() => {
        if (!subAccount) {
            setValue('sub_account_id', 0);
        }
    }, [subAccount]);

    useEffect(() => {
        if (!ttlEnabled) {
            setValue('ttl_date', null);
            setValue('message_ttl', '');
        }
    }, [ttlEnabled, setValue]);


    return (
        <div className="modal fade" ref={modalCreateLink} data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog" role="document">
                <form onSubmit={handleSubmit(submit)}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">{t("platform.channels.links-page.create-link")}</h4>
                            <button type="button" className="btn-close" onClick={() => onRequestClose(false)}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <Switch label={t("platform.channels.links-page.create-modal.advanced-follow-label")} {...register('advanced_follow')} />
                            </div>

                            {advancedFollow && (
                                <>
                                    <div className="mb-3">
                                        <Input label={t("platform.channels.links-page.create-modal.label-label")} placeholder={t("platform.channels.links-page.create-modal.label-placeholder")} errors={errors.label} {...register('label', { required: true })} />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">{t("platform.channels.links-page.create-modal.trigger-label")}</label>
                                        <Controller
                                            control={control}
                                            name='trigger'
                                            rules={{ required: true }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <>
                                                    <Select
                                                        options={opciones}
                                                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                                                        classNamePrefix='form-select-custom'
                                                        placeholder={t("platform.channels.links-page.create-modal.trigger-placeholder")}
                                                        onChange={(selectedOption) => { onChange(selectedOption.value); }}
                                                        value={opciones.find(option => option.value === value) || null}
                                                    />
                                                    <InvalidFeedback error={error} />
                                                </>
                                            )}
                                        />
                                    </div>

                                    {trigger === 'sent_subaccount' && (
                                        <div className="mb-3">
                                            <label className="form-label">{t("platform.channels.links-page.create-modal.subaccount-label")}</label>
                                            <Controller
                                                control={control}
                                                name='sub_account_id'
                                                rules={{ required: true }}
                                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                    <>
                                                        <Select
                                                            options={subAccounts}
                                                            className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                                                            classNamePrefix='form-select-custom'
                                                            placeholder={t("platform.channels.links-page.create-modal.subaccount-placeholder")}
                                                            onChange={(selectedOption) => onChange(selectedOption.value)}
                                                            value={subAccounts.find(option => option.value === value) || null}
                                                        />
                                                        <InvalidFeedback error={error} />
                                                    </>
                                                )}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="mb-3">
                                <TextArea placeholder={t("platform.channels.links-page.create-modal.message-placeholder")} label={t("platform.channels.links-page.message-label")} errors={errors.message}  {...register('message', { required: true })} />
                                <p className="m-0 mt-3">{t("platform.channels.links-page.message-p")}</p>
                            </div>
                            <div className="mb-3">
                                <Switch
                                    label={t("platform.channels.links-page.create-modal.ttl-switch")}
                                    {...register('ttl_enabled')}
                                />
                                {ttlEnabled && (
                                    <>
                                        <div className="mt-3 mb-5">
                                            <p className="text-muted small mb-3">
                                                {t("platform.channels.links-page.create-modal.ttl-help")}
                                            </p>
                                            <Controller
                                                control={control}
                                                name='ttl_date'
                                                rules={{
                                                    required: ttlEnabled ? t("platform.channels.links-page.create-modal.ttl-required") : false
                                                }}
                                                render={({ field: { value }, fieldState: { error } }) => (
                                                    <div className="d-flex gap-4 align-items-center justify-content-center">
                                                        <div className="row d-flex align-items-center">
                                                            <div className="col-12">
                                                                <Calendar
                                                                    selectedDate={value}
                                                                    onDateChange={handleTTLDateChange}
                                                                    minDate={new Date()}
                                                                />
                                                                <InvalidFeedback error={error} />
                                                            </div>
                                                        </div>
                                                        {value && (
                                                            <div className="col-4 d-flex align-items-center">
                                                                <div className="card border-0 bg-light w-100">
                                                                    <div className="card-body d-flex flex-column justify-content-center">
                                                                        <div className="text-center">
                                                                            <i className="bi bi-calendar-check text-success fs-1 mb-3"></i>
                                                                            <h6 className="text-primary mb-2">{t("platform.channels.links-page.create-modal.ttl-picked-title")}</h6>
                                                                            <p className="text-dark fw-bold mb-1">
                                                                                {value.toLocaleDateString('es-ES', {
                                                                                    day: 'numeric',
                                                                                    month: 'long',
                                                                                    year: 'numeric'
                                                                                })}
                                                                            </p>
                                                                            <small className="text-muted">
                                                                                {value.toLocaleDateString('es-ES', { weekday: 'long' })}
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <TextArea label={t("platform.channels.links-page.create-modal.message-ttl-title")} placeholder={t("platform.channels.links-page.create-modal.message-ttl-placeholder")} errors={errors.message_ttl} {...register('message_ttl', { required: false })} />
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* <div className="mb-3">
                                <label className="form-label" >Canal *</label>
                                <select className={'form-control ' + (errors?.app ? 'is-invalid' : '')}  {...register('app', { required: true })}>
                                    <option disabled value={''}>Elija una opción</option>
                                    <option value={'waweb'}>WhatsApp Estandar</option>
                                    <option value={'wa-business'}>WhatsApp Business</option>
                                </select>
                                <InvalidFeedback error={errors.app} />
                            </div> */}

                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white btn-sm" onClick={() => onRequestClose(false)}>{t("btn-cancel")}</button>
                            <Button type="submit" className="btn btn-primary btn-sm" disabled={!isValid} isLoading={isSubmitting} onClick={handleSubmit(submit)}>{t("btn-save")}</Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CreateLinkModal
